/**
 * Auth manifest aggregator — computes protectedRoutes from page-otter, geo-otter,
 * dashboard-otter, and workflow manifest files.
 *
 * Implements the manifest-consumption algorithm described in the auth-otter prompt
 * (swp-ume3, P2.1) and the both-forms invariant (swp-n7kk, P2.2).
 *
 * These helpers serve two roles:
 *   1. Used by pipeline.ts validate_artifact (phase: 'auth') for the orphan-pattern
 *      cross-check: manifest slugs are extracted to detect auth-otter-guessed patterns.
 *   2. Tested directly in validate-auth-config.test.ts for the lowestRole algorithm
 *      and uncoveredSlugs telemetry (integration fixture: DHL run-8 manifests).
 *
 * Refs: swp-ume3 (manifest consumption), swp-n7kk (both-forms invariant)
 */

import { existsSync, readFileSync, readdirSync } from 'fs';
import { join } from 'path';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ManifestPageEntry {
  slug: string;
  authRequired?: boolean;
  requiredRoles?: string[];
}

export interface DerivedProtectedRoute {
  pattern: string;
  requiredRole: string;
}

export interface UncoveredSlug {
  source: string;
  slug: string;
  reason: string;
}

export interface AuthAggregateResult {
  protectedRoutes: DerivedProtectedRoute[];
  uncoveredSlugs: UncoveredSlug[];
}

// ---------------------------------------------------------------------------
// Core algorithms
// ---------------------------------------------------------------------------

/**
 * Compute the lowest-privilege role from a set of required roles.
 *
 * The RBAC hierarchy is expressed as an ordered array of role names from
 * HIGHEST privilege (index 0) to LOWEST privilege (index N). The "lowest"
 * role within a required-roles set is the one that appears LAST in the
 * hierarchy array — it's the minimum-privilege role that still grants access.
 *
 * Since higher roles inherit lower-role access, setting `requiredRole` to the
 * LOWEST role in the set is correct for Next.js middleware: any user with
 * that role OR a higher one passes the gate.
 *
 * Example (DHL hierarchy: ESF8_COORDINATOR[0] > HOSPITAL_EM[1] >
 *          DIALYSIS_MANAGER[2] > FIELD_COORDINATOR[3] > AUDITOR[4] > OBSERVER[5]):
 *
 *   computeLowestRole(
 *     ['ESF8_COORDINATOR', 'HOSPITAL_EM', 'DIALYSIS_MANAGER'],
 *     ['ESF8_COORDINATOR', 'HOSPITAL_EM', 'DIALYSIS_MANAGER', 'FIELD_COORDINATOR', 'AUDITOR', 'OBSERVER']
 *   )
 *   // → 'DIALYSIS_MANAGER'  (index 2 is the highest index in the set)
 *
 *   computeLowestRole(['ESF8_COORDINATOR'], [...])
 *   // → 'ESF8_COORDINATOR'  (single-role set — trivial)
 *
 * @param requiredRoles Non-empty array of roles that can access the page.
 * @param rbacRoles     Full ordered hierarchy, highest → lowest privilege.
 * @returns The lowest-privilege role in `requiredRoles`.
 */
export function computeLowestRole(requiredRoles: string[], rbacRoles: string[]): string {
  let lowestIndex = -1;
  let lowestRole = requiredRoles[0]!;

  for (const role of requiredRoles) {
    const idx = rbacRoles.indexOf(role);
    if (idx > lowestIndex) {
      lowestIndex = idx;
      lowestRole = role;
    }
  }

  return lowestRole;
}

/**
 * Extract the first path segment from a protectedRoute pattern.
 *
 * Used by the orphan-pattern cross-check to compare route bases against
 * known manifest slugs.
 *
 * Examples:
 *   /patient-list          → 'patient-list'
 *   /patient-list/:path*   → 'patient-list'
 *   /dispatch/[id]         → 'dispatch'
 *   /admin/:path*          → 'admin'
 */
export function extractPatternBaseSlug(pattern: string): string | null {
  const stripped = pattern.replace(/^\//, '');
  if (!stripped) return null;
  return stripped.split('/')[0] ?? null;
}

/**
 * Collect all page slugs (first-path-segment only) from every `*-manifest.json`
 * file in artifactsDir. Returns a Set of base slug values.
 *
 * Examples of slug → base-slug:
 *   'patient-list'    → 'patient-list'
 *   'facilities/[id]' → 'facilities'
 *   'dashboard'       → 'dashboard'
 *
 * Used by the pipeline.ts auth validator orphan-pattern cross-check.
 */
export function extractManifestSlugs(artifactsDir: string): Set<string> {
  const slugs = new Set<string>();
  if (!existsSync(artifactsDir)) return slugs;

  let entries: string[];
  try {
    entries = readdirSync(artifactsDir);
  } catch {
    return slugs;
  }

  for (const entry of entries) {
    if (!entry.endsWith('-manifest.json')) continue;
    try {
      const content = JSON.parse(readFileSync(join(artifactsDir, entry), 'utf-8')) as {
        pages?: Array<{ slug?: string }>;
      };
      const pages = content.pages;
      if (!Array.isArray(pages)) continue;
      for (const page of pages) {
        if (typeof page.slug !== 'string' || !page.slug) continue;
        // First path segment covers 'facilities/[id]' → 'facilities'
        const firstSeg = page.slug.split('/')[0];
        if (firstSeg) slugs.add(firstSeg);
        // Also add full slug for direct bare-slug matches (no dynamic segments)
        if (!page.slug.includes('[') && !page.slug.includes(':')) {
          slugs.add(page.slug);
        }
      }
    } catch {
      // Malformed manifest — skip silently
    }
  }

  return slugs;
}

/**
 * Normalize a Next.js file-system routing slug to Next.js middleware matcher syntax.
 *
 * Next.js middleware matchers use Express-like colon params (`:param`) while the
 * file-system uses bracket notation (`[param]`). These are NOT interchangeable —
 * bracket notation in a middleware pattern either silently never matches or throws
 * at server startup (swp-moe0 runtime concern).
 *
 * Transformations (catch-all rules applied first to avoid partial rewrites):
 *   `[[...param]]` → `:param*`  optional catch-all
 *   `[...param]`   → `:param*`  required catch-all
 *   `[param]`      → `:param`   single dynamic segment
 *
 * Static slugs (no brackets) are returned unchanged.
 *
 * Examples:
 *   `facilities/[id]`     → `facilities/:id`
 *   `dispatch/[id]`       → `dispatch/:id`
 *   `blog/[...slug]`      → `blog/:slug*`
 *   `docs/[[...path]]`    → `docs/:path*`
 *   `nested/[a]/[b]/[c]` → `nested/:a/:b/:c`
 *   `patient-list`        → `patient-list`  (pass-through)
 *
 * Refs: swp-moe0 (normalization), swp-52lk (surfaced the incompatibility)
 */
export function normalizeNextJsSlug(slug: string): string {
  return slug
    .replace(/\[\[\.\.\.([\w]+)\]\]/g, ':$1*') // [[...param]] → :param* (optional catch-all)
    .replace(/\[\.\.\.([\w]+)\]/g, ':$1*') //    [...param]   → :param* (required catch-all)
    .replace(/\[([\w]+)\]/g, ':$1'); //           [param]      → :param  (single segment)
}

/**
 * Derive protectedRoutes and uncoveredSlugs from a flat list of manifest page entries.
 *
 * This mirrors the algorithm described in the auth-otter prompt (swp-ume3).
 * Rules per entry:
 *   - authRequired: false          → skip (explicitly public, nothing emitted)
 *   - authRequired: true + roles   → emit BOTH /{slug} AND /{slug}/:path* (swp-n7kk)
 *                                    using lowestRole per rbacRoles hierarchy
 *   - otherwise (missing fields)   → uncoveredSlugs entry, NO protectedRoute emitted
 *
 * Deduplicates by slug — first occurrence wins (pages-manifest takes priority
 * over geo/dashboard for overlapping slugs, since it carries auth fields).
 *
 * @param pages     Flat list of page entries with their source filename.
 * @param rbacRoles Ordered RBAC hierarchy (highest privilege first).
 */
export function deriveProtectedRoutes(
  pages: Array<ManifestPageEntry & { source: string }>,
  rbacRoles: string[]
): AuthAggregateResult {
  const protectedRoutes: DerivedProtectedRoute[] = [];
  const uncoveredSlugs: UncoveredSlug[] = [];
  const seen = new Set<string>();

  for (const page of pages) {
    if (seen.has(page.slug)) continue;
    seen.add(page.slug);

    // Explicitly public — skip entirely (not an uncovered slug)
    if (page.authRequired === false) continue;

    if (
      page.authRequired === true &&
      Array.isArray(page.requiredRoles) &&
      page.requiredRoles.length > 0
    ) {
      // Normalize [bracket] syntax → :colon syntax for Next.js middleware (swp-moe0)
      const normalizedSlug = normalizeNextJsSlug(page.slug);
      const lowestRole = computeLowestRole(page.requiredRoles, rbacRoles);

      protectedRoutes.push({ pattern: `/${normalizedSlug}`, requiredRole: lowestRole });

      // Both-forms invariant (swp-n7kk): also emit /:path* companion for defense-in-depth.
      // EXCEPTION: catch-all slugs (normalized to :param*) already cover arbitrary sub-paths;
      // appending /:path* after a wildcard produces a pattern that can never additionally
      // match anything — skip it to avoid emitting nonsensical middleware matchers.
      if (!normalizedSlug.endsWith('*')) {
        protectedRoutes.push({ pattern: `/${normalizedSlug}/:path*`, requiredRole: lowestRole });
      }
      continue;
    }

    // Missing / incomplete auth declaration → telemetry only, no route emitted
    const reason =
      page.authRequired === true
        ? 'authRequired: true but requiredRoles is missing or empty'
        : 'page emitter did not declare authRequired + requiredRoles';

    uncoveredSlugs.push({ source: page.source, slug: page.slug, reason });
  }

  return { protectedRoutes, uncoveredSlugs };
}
